import { snakeCase } from 'lodash';

import { getLowestLevelAssetIds } from 'components/choosers/ComponentChooser/helpers';
import {
  getTableColumnFilterValues,
  getTableSiteFilterIds,
} from 'components/data/helpers/tableFilterUtils';
import { type TQueryFilter } from 'components/DataTable/types';
import {
  type AtlasGqlAsset,
  type AtlasGqlDamageInspectTask,
  type AtlasGqlDamageRepairTask,
  type AtlasGqlInspectionTask,
  type AtlasGqlInternalBladeInspectionTask,
  type AtlasGqlOtherAssetTask,
  type AtlasGqlTask,
  type AtlasGqlWorkOrder,
  AtlasGqlFilterOperator,
} from 'types/atlas-graphql';

// Work Orders already used `asset`; Tasks persist the Component column under `component`.
// Support both buckets so existing saved table state keeps working after the filter move.
const COMPONENT_FILTER_COLUMN_KEYS = ['asset', 'component'];

type WorkOrderTask =
  | AtlasGqlInspectionTask
  | AtlasGqlInternalBladeInspectionTask
  | AtlasGqlOtherAssetTask
  | AtlasGqlDamageRepairTask
  | AtlasGqlDamageInspectTask;

type TaskHierarchyAsset = AtlasGqlAsset & { __typename?: string };

const getComponentFilterValues = (
  filters: Record<string, unknown> | undefined,
  filterKey: string
): string[] => {
  for (const columnKey of COMPONENT_FILTER_COLUMN_KEYS) {
    const values = getTableColumnFilterValues(filters, columnKey, filterKey);
    if (values.length) {
      return values;
    }
  }

  return [];
};

export const getWorkOrderTableAssetIds = (
  filters: Record<string, unknown> | undefined
): string[] | undefined => {
  const siteIds = getTableSiteFilterIds(filters);
  const turbineIds = getTableColumnFilterValues(filters, 'turbine');
  const componentIds = getComponentFilterValues(filters, 'asset');

  return getLowestLevelAssetIds(
    siteIds.length ? siteIds : undefined,
    turbineIds.length ? turbineIds : undefined,
    componentIds.length ? componentIds : undefined
  );
};

export const getWorkOrderTableComponentTypes = (
  filters: Record<string, unknown> | undefined
): string[] | undefined => {
  const componentTypes = getComponentFilterValues(filters, 'componentType');
  return componentTypes.length ? componentTypes : undefined;
};

// Derived server inputs shared by the Work Order and Task tables, which both translate their
// Site/Turbine/Component column filters into `assetIds` and an `assetType` universal filter.
export const getWorkOrderTableAssetVariables = (
  filters: Record<string, unknown> | undefined
): { assetIds: string[] | undefined } => ({
  assetIds: getWorkOrderTableAssetIds(filters),
});

export const getWorkOrderTableComponentTypeFilter = (
  filters: Record<string, unknown> | undefined
): TQueryFilter[] => {
  const componentTypes = getWorkOrderTableComponentTypes(filters);

  return componentTypes
    ? [{ key: 'assetType', operator: AtlasGqlFilterOperator.Equals, values: componentTypes }]
    : [];
};

export const getTaskAssetHierarchy = (
  task: AtlasGqlTask | null | undefined
): TaskHierarchyAsset[] => {
  const asset = (task as WorkOrderTask | null | undefined)?.asset;

  if (!asset) {
    return [];
  }

  return [asset, ...(asset.ancestors ?? [])].filter(
    (hierarchyAsset): hierarchyAsset is TaskHierarchyAsset => Boolean(hierarchyAsset)
  );
};

const getNormalizedAssetTypeName = (asset: TaskHierarchyAsset): string | undefined =>
  asset?.assetType?.name ? snakeCase(asset?.assetType?.name) : undefined;

export const isWorkOrderMatchingAssetFilter = (
  workOrder: AtlasGqlWorkOrder,
  assetIds: string[] | undefined
): boolean => {
  if (!assetIds?.length) {
    return true;
  }

  const assetIdSet = new Set(assetIds);

  return (
    workOrder.tasks?.some((task) =>
      getTaskAssetHierarchy(task).some((asset) => assetIdSet.has(asset.id))
    ) ?? false
  );
};

export const isWorkOrderMatchingComponentTypeFilter = (
  workOrder: AtlasGqlWorkOrder,
  componentTypes: string[] | undefined
): boolean => {
  if (!componentTypes?.length) {
    return true;
  }

  const componentTypeSet = new Set(componentTypes);

  return (
    workOrder.tasks?.some((task) =>
      getTaskAssetHierarchy(task).some((asset) => {
        const assetType = getNormalizedAssetTypeName(asset);
        return assetType ? componentTypeSet.has(assetType) : false;
      })
    ) ?? false
  );
};
